/**
 * `GET /api/me` — the signed-in staff member, their capabilities, and their MFA
 * status as computed by the server's MFA gate (`apps/portal/src/server/mfa.ts`:
 * `{ required, enrolled, aal, enforced, graceEndsAt }`, `graceEndsAt` an ISO
 * string or null). `mfa` is kept optional: if a server ever omits it, nothing
 * is gated.
 */
import {
  PORTAL_CAPABILITIES,
  isPortalRole,
  type PortalCapability,
  type PortalRole,
} from "../../shared/roles.js";

export type Aal = "aal1" | "aal2";

export interface MfaStatus {
  /** Effective policy: this user must have TOTP while enforcement is enabled. */
  required: boolean;
  /** A verified TOTP factor exists. */
  enrolled: boolean;
  /** Assurance level of the current session token. */
  aal: Aal;
  /** The server is currently requiring aal2 for every other route. */
  enforced: boolean;
  /** When the enrollment grace window ends (ISO), or null if not applicable. */
  graceEndsAt: string | null;
}

export interface MeParty {
  id: string;
  displayName: string;
}

export type MeScope = { all: true } | { partyIds: string[] };

export interface Me {
  viewAs?: ViewAs;
  /** Directory identity, not the Supabase subject; presentation only. */
  userId?: string;
  /** Current authentication subject; presentation of maker/checker restrictions only. */
  authUserId?: string;
  email: string | null;
  role: PortalRole | null;
  capabilities: PortalCapability[];
  mfa?: MfaStatus;
  party?: MeParty | null;
  scope?: MeScope;
}

export interface ViewAs {
  userId: string;
  displayName: string;
  adminUserId: string;
  adminEmail: string | null;
  readOnly: true;
}

export function normalizeViewAs(raw: unknown): ViewAs | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const value = raw as Record<string, unknown>;
  if (value.readOnly !== true || typeof value.userId !== "string" || typeof value.displayName !== "string" || typeof value.adminUserId !== "string" || !(value.adminEmail === null || typeof value.adminEmail === "string")) return undefined;
  return { userId: value.userId, displayName: value.displayName, adminUserId: value.adminUserId, adminEmail: value.adminEmail, readOnly: true };
}

export function normalizeMe(raw: unknown): Me {
  const r = (raw ?? {}) as Record<string, unknown>;
  const role = typeof r.role === "string" && isPortalRole(r.role) ? r.role : null;
  const capabilities = Array.isArray(r.capabilities)
    ? r.capabilities.filter((c): c is PortalCapability =>
        (PORTAL_CAPABILITIES as readonly string[]).includes(c as string),
      )
    : [];
  const mfa = normalizeMfa(r.mfa);
  const party = normalizeParty(r.party);
  const scope = normalizeScope(r.scope);
  return {
    ...(normalizeViewAs(r.viewAs) ? { viewAs: normalizeViewAs(r.viewAs) } : {}),
    ...(typeof r.userId === "string" && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(r.userId) ? { userId: r.userId } : {}),
    ...(typeof r.authUserId === "string" && r.authUserId.length > 0 && r.authUserId.length <= 128 ? { authUserId: r.authUserId } : {}),
    email: typeof r.email === "string" ? r.email : null,
    role,
    capabilities,
    ...(mfa ? { mfa } : {}),
    ...(party !== undefined ? { party } : {}),
    ...(scope ? { scope } : {}),
  };
}

function normalizeParty(raw: unknown): MeParty | null | undefined {
  if (raw === null) return null;
  if (!raw || typeof raw !== "object") return undefined;
  const p = raw as Record<string, unknown>;
  if (typeof p.id !== "string" || typeof p.displayName !== "string") return undefined;
  return { id: p.id, displayName: p.displayName };
}

function normalizeScope(raw: unknown): MeScope | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const s = raw as Record<string, unknown>;
  if (s.all === true) return { all: true };
  if (Array.isArray(s.partyIds) && s.partyIds.every((id) => typeof id === "string")) {
    return { partyIds: s.partyIds as string[] };
  }
  return undefined;
}

function normalizeMfa(raw: unknown): MfaStatus | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const m = raw as Record<string, unknown>;
  return {
    required: m.required === true,
    enrolled: m.enrolled === true,
    aal: m.aal === "aal2" ? "aal2" : "aal1",
    enforced: m.enforced === true,
    graceEndsAt: typeof m.graceEndsAt === "string" ? m.graceEndsAt : null,
  };
}

/** Route guard: send the user to /mfa when the server enforces aal2 and the session is aal1. */
export function mfaRedirectNeeded(me: Pick<Me, "mfa"> | null | undefined): boolean {
  const mfa = me?.mfa;
  if (!mfa) return false;
  return mfa.enforced && mfa.aal !== "aal2";
}

/** Soft prompt: effective policy requires enrollment but still permits the grace window. */
export function mfaEnrollmentPending(me: Pick<Me, "mfa"> | null | undefined): boolean {
  const mfa = me?.mfa;
  if (!mfa) return false;
  return mfa.required && !mfa.enrolled && !mfa.enforced;
}

export function hasCapability(me: Pick<Me, "capabilities"> | null | undefined, cap: PortalCapability): boolean {
  return me?.capabilities.includes(cap) ?? false;
}
